<template>
    <section class="slice slice-content_suggestions">
        <h2 class="slice-content_suggestions__title">{{ slice.primary.title }}</h2>
        <div class="slice-content_suggestions__content">
            <div v-for="(item, i) in items" :key="`content-suggestions-item--${i}`" class="slice-content_suggestions__content-item">
                <ArticleCard
                    v-if="item.type === 'article'"
                    :article="item as FilledContentRelationshipField<'article', string, ArticleDocumentData> | ArticleDocument"
                />
                <CollectionThumbnail
                    v-else-if="item.type === 'collection'"
                    :collection="item as FilledContentRelationshipField<'collection', string, CollectionDocumentData> | CollectionDocument"
                />
                <EventCard
                    v-else-if="item.type === 'event'"
                    :event="item as FilledContentRelationshipField<'event', string, EventDocumentData> | EventDocument"
                />
                <GuideCard
                    v-else-if="item.type === 'guide'"
                    :guide="item as FilledContentRelationshipField<'guide', string, GuideDocumentData> | GuideDocument"
                />
                <ArticleCard
                    v-else-if="item.type === 'successStory'"
                    :article="item as FilledContentRelationshipField<'successStory', string, SuccessStoryDocumentData> | SuccessStoryDocument"
                />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {isFilled} from '@prismicio/helpers';
    import type {FilledContentRelationshipField} from '@prismicio/types';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {
        ArticleDocument,
        ArticleDocumentData,
        CollectionDocument,
        CollectionDocumentData,
        ContentSuggestionsSlice,
        EventDocument,
        EventDocumentData,
        GuideDocument,
        GuideDocumentData,
        ResourceCategoryDocumentData,
        SuccessStoryDocument,
        SuccessStoryDocumentData,
    } from '@prismicio-types';
    import {inject, ref, useAsyncData} from '#imports';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import ArticleCard from '~/components/ArticleCard.vue';
    import CollectionThumbnail from '~/components/CollectionThumbnail.vue';
    import EventCard from '~/components/EventCard.vue';
    import GuideCard from '~/components/GuideCard.vue';
    import usePrismicSearch from '~/composables/content/usePrismicSearch';

    const props = defineProps(getSliceComponentProps<ContentSuggestionsSlice>(['slice', 'index', 'slices', 'context']));

    const currentDocument = inject(PRISMIC_DOCUMENT, ref());

    const POSSIBLE_TYPES = ['article', 'collection', 'event', 'guide', 'successStory'];
    type PossibleTypes = (typeof POSSIBLE_TYPES)[number];
    type PossibleDocumentDataTypes = ArticleDocumentData | CollectionDocumentData | EventDocumentData | GuideDocumentData | SuccessStoryDocumentData;
    type PossibleDocumentTypes = ArticleDocument | CollectionDocument | EventDocument | GuideDocument | SuccessStoryDocument;

    function fetchSuggestions(type: 'all' | PossibleTypes, category: string | undefined) {
        let excludes: string[] | undefined;
        // Exclude current document from suggestions
        if (currentDocument.value?.uid) {
            excludes = [currentDocument.value.uid];
        }

        return () => {
            const queries = [];
            if (type === 'all') {
                queries.push(
                    ...POSSIBLE_TYPES.map((type) =>
                        usePrismicSearch({
                            type,
                            pageSize: 3,
                            category,
                            excludes,
                        }),
                    ),
                );
            } else {
                queries.push(
                    usePrismicSearch({
                        type,
                        pageSize: 3,
                        category,
                        excludes,
                    }),
                );
            }

            return Promise.all(queries);
        };
    }

    const items: (FilledContentRelationshipField<PossibleTypes, string, PossibleDocumentDataTypes> | PossibleDocumentTypes)[] = [];
    if (props.slice.variation === 'default') {
        // curated
        const filteredContent = [props.slice.primary.content1, props.slice.primary.content2, props.slice.primary.content3]
            .filter((content) => content !== undefined)
            .filter((content) => isFilled.contentRelationship(content)) as FilledContentRelationshipField<
            PossibleTypes,
            string,
            PossibleDocumentDataTypes
        >[];
        items.push(...filteredContent);
    } else if (props.slice.variation === 'automated') {
        // automated
        const {data: suggestions} = await useAsyncData(
            'content-suggestions',
            fetchSuggestions(
                props.slice.primary.contentType,
                isFilled.contentRelationship(props.slice.primary.category)
                    ? String((props.slice.primary.category.data as ResourceCategoryDocumentData)?.name || '') || undefined
                    : undefined,
            ),
            {deep: true},
        );

        if (suggestions.value) {
            const mostRecentSuggestions = suggestions.value
                .map((query) => query?.results)
                .flat()
                .filter((suggestion) => suggestion !== undefined)
                .sort((a, b) => new Date(b?.data?.releaseDate).getTime() - new Date(a?.data?.releaseDate).getTime()) as PossibleDocumentTypes[];

            if (props.slice.primary.suggestionsFromSameContentType) {
                const mostRecentSuggestionType = mostRecentSuggestions[0].type;
                items.push(...mostRecentSuggestions.filter((suggestion) => suggestion.type === mostRecentSuggestionType).slice(0, 3));
            } else {
                items.push(...mostRecentSuggestions.slice(0, 3));
            }
        }
    }
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-content_suggestions {
        margin-top: var(--joy-core-spacing-12);
        padding: var(--joy-core-spacing-6);
        background-color: var(--joy-color-quaternary-10);

        &__title {
            text-align: center;
            margin-bottom: var(--joy-core-spacing-8);
        }

        &__content {
            display: grid;
            align-items: center;
            gap: var(--joy-core-spacing-6);

            @include content-width;

            @include mq.screen_md(min) {
                grid-template-columns: repeat(auto-fit, calc(33.33% - var(--joy-core-spacing-6)));
                align-items: start;
                justify-content: center;
            }
        }

        /* Themes */
        .resource-page--corporate &,
        .resource-page--strategy & {
            background-color: var(--joy-color-neutral-20);
        }
    }
</style>
